import { useId } from "react";

import type { InputMaybe } from "@/lib/gql/__generated__/graphql";
import { StockTypeTarget, StockTypeTerm } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types";
import { QuestionSectionWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/_layouts/item-wrapper";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";
import { Image } from "@/components/ui/image/image";

type Props = {
	isEdited: boolean;
	hasError: boolean;

	stockTypeTerm: ForNodeType<InputMaybe<StockTypeTerm> | undefined> | undefined;
	stockTypeTarget: ForNodeType<InputMaybe<StockTypeTarget> | undefined> | undefined;
	maxStockNum: ForNodeType<InputMaybe<number> | undefined> | undefined;

	onSetMaxStockNum: (input: number | undefined) => void;
	onSetStockTypeTarget: (input: StockTypeTarget) => void;
	onSetStockTypeTerm: (input: StockTypeTerm) => void;
};

export const QuestionPlanStockTerm = ({
	isEdited,
	hasError,
	stockTypeTarget,
	stockTypeTerm,
	maxStockNum,
	onSetMaxStockNum: _onSetMaxStockNum,
	onSetStockTypeTarget: _onSetStockTypeTarget,
	onSetStockTypeTerm: _onSetStockTypeTerm,
}: Props) => {
	const uniqueID = useId();
	const stockTypeTermOptions = [
		{
			id: `stockTypeTerm_0_${uniqueID}`,
			label: "１泊ごとにカウントし",
			value: StockTypeTerm.PerDate,
		},
		{
			id: `stockTypeTerm_1_${uniqueID}`,
			label: "１予約ごとにカウントし",
			value: StockTypeTerm.PerReservation,
		},
	] satisfies FormOptionType[];

	const onSetStockTypeTerm = (e: string) => {
		_onSetStockTypeTerm(e as StockTypeTerm);
	};

	const onSetMaxStockNum = (e: number | undefined) => {
		_onSetMaxStockNum(e);
	};

	const stockTypeTargetOptions = [
		{
			id: `stockTypeTarget_0_${uniqueID}`,
			label: "人",
			value: StockTypeTarget.Person,
		},
		{
			id: `stockTypeTarget_1_${uniqueID}`,
			label: "部屋",
			value: StockTypeTarget.Room,
		},
		{
			id: `stockTypeTarget_2_${uniqueID}`,
			label: "団体",
			value: StockTypeTarget.Group,
		},
	] satisfies FormOptionType[];

	const onSetStockTypeTarget = (e: string) => {
		_onSetStockTypeTarget(e as StockTypeTarget);
	};

	const guide =
		"販売期間内でこのプランを提供できる数に上限がある場合は、上限数と単位をこちらで入力してください。<br/>上の質問で「1日の在庫数上限」を設定している場合、単位は同じものが適用されます。";

	const accordionId = `stock-term-accordion_${uniqueID}`;

	return (
		<Accordion type="single" collapsible>
			<AccordionItem value={accordionId} borderBottom={false}>
				<AccordionTrigger arrowLeft className="hover:no-underline">
					<span className="text-base font-semibold">販売期間中に提供できる通算在庫数</span>
				</AccordionTrigger>
				<AccordionContent>
					<div className="flex flex-col gap-y-6">
						<div className="overflow-hidden rounded-lg border border-zinc-200">
							<Image
								src="/assets/room/description-stock.png"
								alt="通算在庫数についての説明"
								className="w-full"
							/>
						</div>
						<FeatureIslandCategoryQuestionForMain
							required={false}
							title={"販売期間中に提供できる通算在庫数"}
							guide={guide}
							isEdited={isEdited}
							hasError={hasError}
							inputChildren={
								<QuestionSectionWrapper>
									<SelectField
										value={stockTypeTerm?.value}
										options={stockTypeTermOptions}
										handleValueChange={onSetStockTypeTerm}
										errorMessage={stockTypeTerm?.error}
									/>

									<div className="flex items-center gap-x-2">
										<p className="text-sm">合計</p>
										<InputNumberField
											value={maxStockNum?.value ?? undefined}
											handleInput={onSetMaxStockNum}
											errorMessage={maxStockNum?.error}
											hiddenSpinButton={false}
										/>
										<SelectField
											value={stockTypeTarget?.value}
											options={stockTypeTargetOptions}
											handleValueChange={onSetStockTypeTarget}
											errorMessage={stockTypeTarget?.error}
										/>
										<p className="text-sm">まで</p>
									</div>
								</QuestionSectionWrapper>
							}
						/>
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};
